import { User } from '../types';

// Organisatie-instellingen voor downloaden en afspelen, uit `capabilities` van
// /api/me. Een oudere backend stuurt de velden niet mee: ontbrekend telt als toegestaan.
export interface MediaAccess {
    canDownloadTranscript: boolean;
    canDownloadAudio: boolean;
    canPlayAudio: boolean;
}

export const getMediaAccess = (user: User | null | undefined): MediaAccess => {
    const capabilities = user?.capabilities;
    return {
        canDownloadTranscript: capabilities?.can_download_transcript !== false,
        canDownloadAudio: capabilities?.can_download_audio !== false,
        canPlayAudio: capabilities?.can_play_audio !== false
    };
};

export type MediaAccessDenialCode =
    | 'transcript_download_disabled'
    | 'audio_download_disabled'
    | 'audio_playback_disabled';

const DENIAL_CODES: readonly string[] = [
    'transcript_download_disabled',
    'audio_download_disabled',
    'audio_playback_disabled'
];

// 403 met een `error`-code: de organisatie blokkeert dit. Niet opnieuw proberen
// en niet terugvallen op een andere route. De bestaande 403's op dezelfde
// endpoints ("Download permission required") hebben geen `error`-veld.
export class MediaAccessDeniedError extends Error {
    readonly code: MediaAccessDenialCode;

    constructor(code: MediaAccessDenialCode, message: string) {
        super(message);
        this.name = 'MediaAccessDeniedError';
        this.code = code;
    }
}

// i18n-sleutel voor de melding; de NL-tekst is gelijk aan `message` van de backend.
export const mediaAccessMessageKey = (code: MediaAccessDenialCode): string =>
    `mediaAccess.errors.${code}`;

const readBlobText = (blob: Blob): Promise<string> => {
    if (typeof blob.text === 'function') {
        return blob.text();
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
};

// Herkent een geblokkeerde download of afspeelactie in een axios-fout. Bij
// responseType 'blob' zit de JSON-body van de 403 in een Blob.
export const toMediaAccessDeniedError = async (error: unknown): Promise<MediaAccessDeniedError | null> => {
    const response = (error as any)?.response;
    if (response?.status !== 403) {
        return null;
    }
    let data: any = response.data;
    try {
        if (typeof Blob !== 'undefined' && data instanceof Blob) {
            data = JSON.parse(await readBlobText(data));
        } else if (typeof data === 'string') {
            data = JSON.parse(data);
        }
    } catch {
        return null;
    }
    const code = data?.error;
    if (typeof code !== 'string' || !DENIAL_CODES.includes(code)) {
        return null;
    }
    const message = typeof data?.message === 'string' ? data.message : code;
    return new MediaAccessDeniedError(code as MediaAccessDenialCode, message);
};
